import React, { ReactNode } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Chip, Stack, SxProps, Theme, Tooltip } from '@mui/material';

interface TagChipProps {
  type: number;
  label: string;
  hideLabel?: boolean;
  hideTooltip?: boolean;
  tooltipTitle?: ReactNode;
  sx?: SxProps<Theme>;
}

const createChipLabel = ({
  label,
  hideLabel,
  hideTooltip,
  tooltipTitle
}: Omit<TagChipProps, 'type'>) => (
  <Stack gap={1} direction="row" alignItems="center">
    {!hideLabel && <Box>{label}</Box>}
    {tooltipTitle && !hideTooltip && <TagTooltip tooltipTitle={tooltipTitle} />}
  </Stack>
);

type ChipStyle = {
  backgroundColor?: string;
  border?: string;
  color?: string;
};

type ChipVariant = 'outlined' | undefined;
type ChipColor = 'primary' | 'success' | 'warning' | 'error' | undefined;
type TagType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

const chipStyles: Record<TagType, ChipStyle> = {
  1: { backgroundColor: '#00000014' },
  2: { backgroundColor: '#E7EEF4', border: 'none' },
  3: { backgroundColor: '#EAF2EA', border: 'none', color: '#569E59' },
  4: { backgroundColor: '#FFE9D4', border: 'none' },
  5: { backgroundColor: '#FFDAD4', border: 'none', color: '#BA1B1B' },
  6: {},
  7: {},
  8: {},
  9: {}
};

const chipVariants: Record<TagType, ChipVariant> = {
  1: undefined,
  2: 'outlined',
  3: undefined,
  4: 'outlined',
  5: 'outlined',
  6: undefined,
  7: undefined,
  8: undefined,
  9: undefined
};

const chipColors: Record<TagType, ChipColor> = {
  1: undefined,
  2: 'primary',
  3: undefined,
  4: 'warning',
  5: undefined,
  6: 'primary',
  7: 'success',
  8: 'warning',
  9: 'error'
};

const createChipComponent = (type: TagType) => {
  const Component = (props: Omit<TagChipProps, 'type'>) => (
    <Chip
      sx={{
        ...chipStyles[type],
        ...props.sx,
        ...(props.hideLabel
          ? {
              '& .MuiChip-label': {
                paddingRight: '4px',
                paddingLeft: '4px'
              }
            }
          : {
              '&:has(> .MuiIcon-root)': {
                paddingRight: '4px'
              }
            })
      }}
      color={chipColors[type]}
      variant={chipVariants[type]}
      label={createChipLabel(props)}
    />
  );
  Component.displayName = `TagChip${type}`;
  return Component;
};

export const TagTypeToChipMapping: {
  [key in TagType]: (props: Omit<TagChipProps, 'type'>) => JSX.Element;
} = {
  1: createChipComponent(1),
  2: createChipComponent(2),
  3: createChipComponent(3),
  4: createChipComponent(4),
  5: createChipComponent(5),
  6: createChipComponent(6),
  7: createChipComponent(7),
  8: createChipComponent(8),
  9: createChipComponent(9)
};

export default function TagChip({
  type,
  label,
  hideLabel,
  tooltipTitle,
  sx,
  hideTooltip
}: TagChipProps) {
  const TagTypeChip = TagTypeToChipMapping[type as TagType];
  if (!TagTypeChip) {
    return null;
  }
  const sxProps = {
    width: 'auto',
    maxWidth: 'fit-content',
    height: '24px',
    fontWeight: 500,
    ...sx
  };
  return (
    <TagTypeChip
      {...{
        label,
        hideLabel,
        tooltipTitle,
        sx: sxProps,
        hideTooltip
      }}
    />
  );
}

const TagTooltip = ({ tooltipTitle }: { tooltipTitle: ReactNode }) => {
  return (
    <Tooltip title={tooltipTitle}>
      <InfoOutlined
        sx={{
          fontSize: '20px'
        }}
      />
    </Tooltip>
  );
};
